'use client';

import * as React from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { cn } from './lib/utils';
import { cva, type VariantProps } from 'class-variance-authority';
import { Tabs as TabsPrimitive } from 'radix-ui';

// Variants for TabsList
const tabsListVariants = cva('flex items-center shrink-0', {
  variants: {
    variant: {
      default: 'bg-accent p-1',
      button: '',
      line: 'border-b border-border',
    },
    shape: {
      default: '',
      pill: '',
    },
    size: {
      lg: 'gap-2.5',
      md: 'gap-2',
      sm: 'gap-1.5',
      xs: 'gap-1',
    },
  },
  compoundVariants: [
    { variant: 'default', size: 'lg', className: 'p-1.5 gap-2.5' },
    { variant: 'default', size: 'md', className: 'p-1 gap-2' },
    { variant: 'default', size: 'sm', className: 'p-1 gap-1.5' },
    { variant: 'default', size: 'xs', className: 'p-1 gap-1' },

    {
      variant: 'default',
      shape: 'default',
      size: 'lg',
      className: 'rounded-lg',
    },
    {
      variant: 'default',
      shape: 'default',
      size: 'md',
      className: 'rounded-lg',
    },
    {
      variant: 'default',
      shape: 'default',
      size: 'sm',
      className: 'rounded-md',
    },
    {
      variant: 'default',
      shape: 'default',
      size: 'xs',
      className: 'rounded-md',
    },

    { variant: 'line', size: 'lg', className: 'gap-9' },
    { variant: 'line', size: 'md', className: 'gap-8' },
    { variant: 'line', size: 'sm', className: 'gap-4' },
    { variant: 'line', size: 'xs', className: 'gap-4' },

    {
      variant: 'default',
      shape: 'pill',
      className: 'rounded-full [&_[role=tab]]:rounded-full',
    },
    {
      variant: 'button',
      shape: 'pill',
      className: 'rounded-full [&_[role=tab]]:rounded-full',
    },
  ],
  defaultVariants: {
    variant: 'default',
    size: 'md',
  },
});

// Variants for TabsTrigger
const tabsTriggerVariants = cva(
  'shrink-0 cursor-pointer whitespace-nowrap inline-flex justify-center items-center font-medium ring-offset-background transition-colors focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 data-disabled:pointer-events-none data-disabled:opacity-50 [&_svg]:shrink-0 [&_svg]:text-muted-foreground [&:hover_svg]:text-primary [&[data-state=active]_svg]:text-primary',
  {
    variants: {
      variant: {
        default:
          'text-muted-foreground data-[state=active]:bg-background hover:text-foreground data-[state=active]:text-foreground data-[state=active]:shadow-xs data-[state=active]:shadow-black/5',
        button:
          'focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 rounded-lg text-accent-foreground hover:text-foreground data-[state=active]:bg-accent data-[state=active]:text-foreground',
        line: 'border-b-2 text-muted-foreground border-transparent data-[state=active]:border-primary hover:text-primary data-[state=active]:text-primary data-[state=active]:border-primary data-[state=active]:text-primary',
      },
      size: {
        lg: 'gap-2.5 [&_svg]:size-5 text-sm',
        md: 'gap-2 [&_svg]:size-4 text-sm',
        sm: 'gap-1.5 [&_svg]:size-3.5 text-xs',
        xs: 'gap-1 [&_svg]:size-3.5 text-xs',
      },
    },
    compoundVariants: [
      { variant: 'default', size: 'lg', className: 'py-2.5 px-4 rounded-md' },
      { variant: 'default', size: 'md', className: 'py-1.5 px-3 rounded-md' },
      { variant: 'default', size: 'sm', className: 'py-1.5 px-2.5 rounded-sm' },
      { variant: 'default', size: 'xs', className: 'py-1 px-2 rounded-sm' },

      { variant: 'button', size: 'lg', className: 'py-3 px-4 rounded-lg' },
      { variant: 'button', size: 'md', className: 'py-2.5 px-3 rounded-lg' },
      { variant: 'button', size: 'sm', className: 'py-2 px-2.5 rounded-md' },
      { variant: 'button', size: 'xs', className: 'py-1.5 px-2 rounded-md' },

      { variant: 'line', size: 'lg', className: 'py-3' },
      { variant: 'line', size: 'md', className: 'py-2.5' },
      { variant: 'line', size: 'sm', className: 'py-2' },
      { variant: 'line', size: 'xs', className: 'py-1.5' },
    ],
    defaultVariants: {
      variant: 'default',
      size: 'md',
    },
  },
);

// Variants for TabsContent
const tabsContentVariants = cva(
  'mt-2.5 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
  {
    variants: {
      variant: {
        default: '',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  },
);

// Context
type TabsContextType = {
  variant?: 'default' | 'button' | 'line';
  size?: 'lg' | 'sm' | 'xs' | 'md';
};
const TabsContext = React.createContext<TabsContextType>({
  variant: 'default',
  size: 'md',
});

// Components
function Tabs({ className, ...props }: React.ComponentProps<typeof TabsPrimitive.Root>) {
  return <TabsPrimitive.Root data-slot="tabs" className={cn('', className)} {...props} />;
}

type TabsListProps = React.ComponentProps<typeof TabsPrimitive.List> &
  VariantProps<typeof tabsListVariants> & {
    /**
     * #1095 — put the strip in a horizontally scrollable viewport instead of
     * letting it clip. Triggers are `shrink-0` + `whitespace-nowrap`, so a strip
     * wider than its container is otherwise cut off with no way to reach the
     * hidden tabs. Set this on any strip whose tab count can outgrow a
     * laptop-width card (boat detail, customer detail, dealership settings).
     */
    scrollable?: boolean;
  };

/**
 * #1095 — report which sides of a scrollable strip still hide tabs.
 *
 * This exists because the scrollbar cannot be relied on as the affordance:
 * macOS defaults `AppleShowScrollBars` to "when scrolling", so at rest an
 * overflowing strip on any trackpad Mac looks like it simply ends at the last
 * visible tab — and `min-w-full` makes that truncation look deliberate. The
 * chevrons and edge fades driven by this hook are the always-on signal.
 */
function useScrollEdges(ref: React.RefObject<HTMLDivElement | null>, enabled: boolean) {
  const [edges, setEdges] = React.useState({ start: false, end: false });

  React.useEffect(() => {
    const el = ref.current;
    if (!enabled || !el) return;

    const update = () => {
      // 1px of slack: fractional layout widths leave a fully-scrolled strip a
      // fraction short of its maximum, which would strand a phantom chevron.
      const max = el.scrollWidth - el.clientWidth;
      setEdges({ start: el.scrollLeft > 1, end: el.scrollLeft < max - 1 });
    };

    update();
    el.addEventListener('scroll', update, { passive: true });

    // The strip's width changes without the viewport resizing whenever a badge
    // count lands or the tab set changes, so observe the list as well.
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(update);
    observer?.observe(el);
    if (el.firstElementChild) observer?.observe(el.firstElementChild);

    return () => {
      el.removeEventListener('scroll', update);
      observer?.disconnect();
    };
  }, [ref, enabled]);

  return edges;
}

function TabsScrollButton({
  direction,
  onActivate,
}: {
  direction: 'start' | 'end';
  onActivate: () => void;
}) {
  const Icon = direction === 'start' ? ChevronLeft : ChevronRight;

  return (
    <>
      {/* Stops a pixel short of the bottom so the `line` variant's underline
          stays continuous underneath rather than fading out at the edges. */}
      <div
        aria-hidden
        className={cn(
          // Opaque under the chevron, fading only past it — a plain edge-to-edge
          // gradient leaves the chevron sitting on half-legible text.
          'pointer-events-none absolute top-0 bottom-px w-16 from-background from-40% to-transparent',
          direction === 'start' ? 'left-0 bg-gradient-to-r' : 'right-0 bg-gradient-to-l',
        )}
      />
      <button
        type="button"
        // Keyboard users already reach every tab through the tablist's own
        // roving arrow-key navigation, which scrolls hidden triggers into view.
        // These are a pointer-only convenience, so they stay out of both the
        // tab order and the accessibility tree.
        aria-hidden
        tabIndex={-1}
        onClick={onActivate}
        className={cn(
          'absolute top-0 bottom-px z-10 flex items-center px-1 cursor-pointer',
          'text-muted-foreground hover:text-foreground transition-colors',
          direction === 'start' ? 'left-0' : 'right-0',
        )}
      >
        <Icon className="size-4" />
      </button>
    </>
  );
}

function TabsList({
  className,
  variant = 'default',
  shape = 'default',
  size = 'md',
  scrollable = false,
  ...props
}: TabsListProps) {
  const viewportRef = React.useRef<HTMLDivElement>(null);
  const edges = useScrollEdges(viewportRef, scrollable);

  const scrollByPage = (dir: 1 | -1) => {
    const el = viewportRef.current;
    if (!el) return;
    // Leave a sliver of the current edge tab on screen so the jump keeps its
    // place rather than swapping the strip out wholesale.
    const delta = dir * el.clientWidth * 0.8;
    if (typeof el.scrollBy === 'function') {
      el.scrollBy({ left: delta, behavior: 'smooth' });
    } else {
      el.scrollLeft += delta;
    }
  };

  const list = (
    <TabsPrimitive.List
      data-slot="tabs-list"
      className={cn(
        tabsListVariants({ variant, shape, size }),
        // `w-max` keeps every trigger at its natural width so none are reachable
        // only by shrinking; `min-w-full` keeps the `line` variant's underline
        // continuous across the card when the strip is narrower than the card.
        scrollable && 'w-max min-w-full',
        className,
      )}
      {...props}
    />
  );

  return (
    <TabsContext.Provider value={{ variant: variant || 'default', size: size || 'md' }}>
      {scrollable ? (
        <div data-slot="tabs-list-scroller" className="relative">
          {/* The native scrollbar is suppressed rather than relied on: it is
              invisible at rest on macOS and, where it does show, it renders a
              chunky bar directly under the underline inside the card. */}
          <div
            ref={viewportRef}
            data-slot="tabs-list-viewport"
            className="overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
          >
            {list}
          </div>
          {edges.start && <TabsScrollButton direction="start" onActivate={() => scrollByPage(-1)} />}
          {edges.end && <TabsScrollButton direction="end" onActivate={() => scrollByPage(1)} />}
        </div>
      ) : (
        list
      )}
    </TabsContext.Provider>
  );
}

function TabsTrigger({ className, ...props }: React.ComponentProps<typeof TabsPrimitive.Trigger>) {
  const { variant, size } = React.useContext(TabsContext);

  return (
    <TabsPrimitive.Trigger
      data-slot="tabs-trigger"
      className={cn(tabsTriggerVariants({ variant, size }), className)}
      {...props}
    />
  );
}

function TabsContent({
  className,
  variant,
  ...props
}: React.ComponentProps<typeof TabsPrimitive.Content> & VariantProps<typeof tabsContentVariants>) {
  return (
    <TabsPrimitive.Content
      data-slot="tabs-content"
      className={cn(tabsContentVariants({ variant }), className)}
      {...props}
    />
  );
}

export { Tabs, TabsContent, TabsList, TabsTrigger };
